import {
    IAprEntity,
    IBrandEntity,
    IChannelEntity,
    IInstalmentEntity,
    IInstalmentPlanEntity,
    IItemsWithCount,
    IProductTypeEntity,
    ISignInResponse,
    IUserEntity,
    KeyValueRecord,
} from '@rasayi-workspace/shared';

import { API } from './api';

export const EMPTY_INITIAL_ITEMS: IItemsWithCount = { count: 0, items: [], page: 1, pageSize: 20 };

export const GetAgents = async (): Promise<{ id: string; name: string }[]> => {
    try {
        const agentData: { data: IItemsWithCount<IUserEntity> } = await API.get('/user/agents');
        return agentData?.data?.items?.map(item => ({
            id: item.id,
            name: (item.first_name ?? '') + ' ' + (item.last_name ?? ''),
        }));
    } catch (error) {
        console.log(error);
        return [];
    }
};
export const GetChannels = async (): Promise<{ id: string; name: string }[]> => {
    try {
        const channelData: { data: IItemsWithCount<IChannelEntity> } = await API.get('/channel');
        return channelData.data.items.map(item => ({
            id: item.id,
            name: item.name,
        }));
    } catch (error) {
        console.log(error);
        return [];
    }
};

export const GetBrands = async (): Promise<{ id: string; name: string; charges: number }[]> => {
    try {
        const response: { data: IItemsWithCount<IBrandEntity> } = await API.get('/brand');        
        return response.data.items.map(item => ({
            id: item.id,
            name: item.name,
            charges: item.brand_charges?.[0]?.charges ?? 0,
        }));
    } catch (error) {
        console.log(error);
        return [];
    }
};

export const GetProductType = async (): Promise<{ id: string; name: string }[]> => {
    try {
        const response: { data: IItemsWithCount<IProductTypeEntity> } = await API.get('/product_type');
        return response.data.items.map(item => ({
            id: item.id,
            name: item.name,
        }));
    } catch (error) {
        console.log(error);
        return [];
    }
};

export const GetInstallmentPlans = async (): Promise<{ id: string; name: string; cycle: number }[]> => {
    try {
        const response: { data: IItemsWithCount<IInstalmentPlanEntity> } = await API.get('/instalment_plan');
        return response.data.items.map(item => ({
            id: item.id,
            name: item.name,
            cycle: item.cycle,
        }));
    } catch (error) {
        console.log(error);
        return [];
    }
};

export const GetAprs = async (): Promise<{ id: string; amount: number; rate: number }[]> => {
    try {
        const response: { data: IItemsWithCount<IAprEntity> } = await API.get('/apr');
        return response.data.items.map(item => ({
            id: item.id,
            amount: item.minimum_amount,
            rate: item.value,
        }));
    } catch (error) {
        console.log(error);
        return [];
    }
};

export const addLead = async (data: KeyValueRecord) => {
    try {
        const response = await API.post('/lead', data, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (error) {
        console.error('Error during API call:', error);
        throw error;
    }
};
export const updateLead = async (data: KeyValueRecord, id: string) => {
    try {
        const response = await API.put(`/lead/${id}`, data, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    } catch (error) {
        console.error('Error during API call:', error);
        throw error;
    }
};

export const getLead = async (id: string) => {
    try {
        const response = await API.get(`/lead/${id}`);

        return response.data;
    } catch (error) {
        console.error('Error during API call:', error);
        throw error;
    }
};
export const getFileFromFilename = async (name: string) => {
    try {
        const response = await API.post(`/file/get`, { name });
        return response.data;
    } catch (error) {
        console.error('Error during API call:', error);
        throw error;
    }
};
export const deleteDocument = async (id: string, docId: string) => {
    try {
        const response = await API.delete(`/lead/${id}/documents/${docId}`);
        return response.data;
    } catch (error) {
        console.error('Error during API call:', error);
        throw error;
    }
};

export const assignToCredit = async (id: string) => {
    try {
        const response = await API.put(`/lead/${id}/assign-credit`);
        return response.data;
    } catch (error) {
        console.error('Error during API call:', error);
        throw error;
    }
};
export const holdLead = async (props: { id: string; emi?: string; status?: string; isHold?: boolean; comment?: string }) => {
    try {
        const { emi, status, isHold = true, comment, id } = props;
        const response = await API.put(`/lead/${id}/hold`, { emi, status, isHold, comment });
        return response.data;
    } catch (error) {
        console.error('Error during API call:', error);
        throw error;
    }
};
export const acceptOrRejectLead = async (props: { id: string; emi?: string; status?: string; isHold?: boolean; comment?: string; rejectionReason?: string }) => {
    try {
        const { emi, status, isHold = true, comment, id, rejectionReason } = props;
        const payload: any = { status };
        if (rejectionReason) {
            payload.rejectionReason = rejectionReason;
        } else {
            payload.emi = emi;
            payload.comment = comment;
        }
        const response = await API.put(`/lead/${id}/approve-reject`, payload);
        return response.data;
    } catch (error) {
        console.error('Error during API call:', error);
        throw error;
    }
};
export const getLeadsCount = async (query: { startDate?: string; endDate?: string }) => {
    try {
        const params = new URLSearchParams();
        if (query.startDate) params.append('startDate', query.startDate);
        if (query.endDate) params.append('endDate', query.endDate);
        const url = `/lead/count-summary${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await API.get(url);
        return response.data;
    } catch (error) {
        console.error('Error during API call:', error);
        throw error;
    }
};
export const getLeadsStatusCount = async (query: { startDate?: string; endDate?: string }) => {
    try {
        const params = new URLSearchParams();
        if (query.startDate) params.append('startDate', query.startDate);
        if (query.endDate) params.append('endDate', query.endDate);
        const url = `/lead/status-count-summary${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await API.get(url);
        return response.data;
    } catch (error) {
        console.error('Error during API call:', error);
        throw error;
    }
};
